import type { ReactNode } from 'react';
import { Button, Icon } from '../primitives';
import type { IconName } from '../primitives';
import { TensionGauge } from './TensionGauge';
import './semantic.groupC.css';

/**
 * Foundation semantic: MinigameHud（2026-06-12 R1: 体験文法の統一）
 *
 * ステージ上部の共通 HUD バー（surface 半透明＋下 border）。19 テンプレで
 * バラバラだった HUD/タイマー表現を 1 つに統一する。
 *
 * レイアウト:
 *  - 左 = アイコン（任意・accent 色）+ タイトル（--mg-text-sm / medium / 1 行省略）
 *  - 右 = チップ群（左から timer → progress → mistakes → help → rightSlot）
 *    - timer: TensionGauge（numeric / mm:ss）。段階色は TensionGauge 内蔵の
 *      75/50/25%（success → hint → error）にそのまま従う（Visual Grammar 準拠）。
 *    - progress: 「n/total」チップ / mistakes: 「ミス n/max」チップ
 *    - onHelp 指定時のみ「?」小ボタン（Button ghost sm・aria-label「説明を見る」）
 *    - rightSlot: 任意の差し込み（スコア表示・一時停止ボタン等）
 *  - モバイル前提で高さ控えめ（min-height 2.5rem）・チップは折返さない min 構成。
 *
 * 前提:
 *  - 親ステージに position（relative 等）が必要（absolute top で上辺に張り付く。
 *   StartGate / JudgementBurst と同じ前提）。
 *
 * 規約:
 *  - hex 直書き禁止。色は mood トークン経由（semantic.groupC.css 側）。
 *  - アイコンは Icon primitive 経由。icon に string を渡すと lucide 名として解釈する。
 */
export interface MinigameHudTimer {
  /** 残り時間 [ms] */
  remainingMs: number;
  /** 制限時間の総量 [ms]。0 以下の場合 timer チップは描画しない */
  totalMs: number;
}

export interface MinigameHudProgress {
  /** 現在の達成数 */
  current: number;
  /** 目標総数 */
  total: number;
}

export interface MinigameHudMistakes {
  /** 現在のミス数 */
  current: number;
  /** 許容ミス上限 */
  max: number;
}

export interface MinigameHudProps {
  /** 左側に出すタイトル（必須）。長い場合は 1 行省略される */
  title: string;
  /** タイトル左のアイコン（任意）。string は lucide の Icon name として解釈 */
  icon?: ReactNode | IconName;
  /** 残り時間チップ。null/未指定で非表示（useMinigameTimer の remainingMs と組み合わせる想定） */
  timer?: MinigameHudTimer | null;
  /** 進捗チップ「n/total」。null/未指定で非表示 */
  progress?: MinigameHudProgress | null;
  /** ミスチップ「ミス n/max」。null/未指定で非表示 */
  mistakes?: MinigameHudMistakes | null;
  /** 指定時のみ「?」（説明を見る）小ボタンを表示する */
  onHelp?: () => void;
  /** チップ群の右端に任意要素を差し込む（スコア・一時停止ボタン等） */
  rightSlot?: ReactNode;
  /** 追加 className（z-index 調整等） */
  className?: string;
}

export function MinigameHud({
  title,
  icon,
  timer,
  progress,
  mistakes,
  onHelp,
  rightSlot,
  className,
}: MinigameHudProps) {
  const classes = ['mg-hud', className].filter(Boolean).join(' ');
  // string は lucide アイコン名として Icon primitive に解決させる（StartGate と同じ流儀）
  const renderedIcon =
    typeof icon === 'string' ? <Icon name={icon as IconName} size={16} /> : icon;
  // totalMs<=0 は TensionGauge 側でフォールバックが効くが、HUD としては
  // 「タイマーなし」と等価なので描画自体をスキップする
  const showTimer = timer != null && timer.totalMs > 0;

  return (
    <div className={classes}>
      <div className="mg-hud__lead">
        {renderedIcon ? (
          <span className="mg-hud__icon" aria-hidden>
            {renderedIcon}
          </span>
        ) : null}
        <span className="mg-hud__title">{title}</span>
      </div>
      <div className="mg-hud__chips">
        {showTimer ? (
          // TensionGauge(mmss) は current を「秒」として解釈するため ms→s に変換する。
          // 段階色しきい値（75/50/25%）は TensionGauge の既定をそのまま使う。
          <TensionGauge
            className="mg-hud__timer"
            variant="numeric"
            format="mmss"
            current={timer.remainingMs / 1000}
            max={timer.totalMs / 1000}
          />
        ) : null}
        {progress ? (
          <span className="mg-hud__chip">
            {progress.current}/{progress.total}
          </span>
        ) : null}
        {mistakes ? (
          <span className="mg-hud__chip">
            ミス {mistakes.current}/{mistakes.max}
          </span>
        ) : null}
        {onHelp ? (
          <Button
            variant="ghost"
            size="sm"
            className="mg-hud__help"
            onClick={onHelp}
            aria-label="説明を見る"
          >
            <Icon name="CircleHelp" size={16} />
          </Button>
        ) : null}
        {rightSlot}
      </div>
    </div>
  );
}
